import { isEqual } from 'lodash';

import { cleanObj } from './clean-obj';
import { deepCleanObj } from './deepCleanObj';
import { parseJSON } from './parse-json';

export type ParsedUserSavedSearchQuery = {
  searchText?: string;
  categoryIds?: number[];
  conditionIds?: number[];
  colorIds?: number[];
  sizes?: string[];
  minPrice?: number;
  maxPrice?: number;
  order?: string;
  brandIds?: number[];
  tag?: string;
  materialIds?: number[];

  // These are not currently used in filters.
  // materialIds?: number[],
};

export type UnparsedUserSavedSearchQuery = {
  categories?: number;
  conditionIds?: number[];
  colorIds?: number[];
  sizes?: string;
  price?: string;
  order?: string;
  materialIds?: number[];
};

export const queryParamsToUserSavedSearchQuery = (searchKey: string, queryParams: Record<string, any>): ParsedUserSavedSearchQuery => {
  const { min: minPrice, max: maxPrice } = parseJSON(queryParams.price) || {};
  const conditions = parseJSON(queryParams.conditions) || [];
  const brands = parseJSON(queryParams.brands) || [];
  const colors = parseJSON(queryParams.colors) || [];
  const materials = parseJSON(queryParams.materials) || [];

  return deepCleanObj(
    {
      searchText: searchKey?.trim(),
      categoryIds: queryParams.categories ? [+queryParams.categories] : null,
      conditionIds: conditions?.map((c) => +c),
      colorIds: colors?.map((c) => +c),
      brandIds: brands?.map((b) => +b),
      sizes: queryParams.sizes?.split('.'),
      minPrice,
      maxPrice,
      order: queryParams.order,
      tag: queryParams.tag,
      materialIds: materials?.map((c) => +c),
    },
    {
      includeNull: true,
      includeUndefined: true,
      includeEmptyArr: true,
      includeEmptyObj: true,
      includeEmptyString: true,
    },
  );
};

const MATCH_DEEP_CLEAN_OPTS = {
  includeNull: true,
  includeUndefined: true,
  includeEmptyArr: true,
  includeEmptyObj: true,
  includeEmptyString: true,
};

/** Case-insensitive tag comparison; two missing tags count as equal. */
export const isSameTag = (a?: string, b?: string): boolean => (a ?? '').toLowerCase() === (b ?? '').toLowerCase();

type CmsTileScope = { tag?: string; categoryId?: number | null; brandId?: number | null };

/** Legacy rows persisted only the tag while the tile's locked brand/category now scope the page. */
export const resolveTileScope = (
  query: Partial<ParsedUserSavedSearchQuery> = {},
  tiles?: CmsTileScope[],
): Partial<ParsedUserSavedSearchQuery> => {
  const tile = query.tag ? tiles?.find((cmsTile) => isSameTag(cmsTile?.tag, query.tag)) : undefined;

  if (!tile) {
    return query;
  }

  const resolvedQuery = { ...query };

  if (tile.brandId && !resolvedQuery.brandIds?.length) {
    resolvedQuery.brandIds = [tile.brandId];
  }

  if (tile.categoryId != null) {
    delete resolvedQuery.tag;

    if (!resolvedQuery.categoryIds?.length) {
      resolvedQuery.categoryIds = [tile.categoryId];
    }
  }

  return resolvedQuery;
};

const normalizeSavedSearchQueryForMatch = (query: Partial<ParsedUserSavedSearchQuery> = {}) =>
  deepCleanObj(
    {
      ...query,
      searchText: query.searchText?.trim()?.toLowerCase(),
      tag: query.tag?.toLowerCase(),
    },
    MATCH_DEEP_CLEAN_OPTS,
  );

/** True when two saved-search queries describe the same search, ignoring tag/searchText casing. */
export const isSameSavedSearchQuery = (a: Partial<ParsedUserSavedSearchQuery> = {}, b: Partial<ParsedUserSavedSearchQuery> = {}): boolean =>
  isEqual(normalizeSavedSearchQueryForMatch(a), normalizeSavedSearchQueryForMatch(b));

export const userSavedSearchQueryToQueryParams = (
  userSavedSearchQueryParams: ParsedUserSavedSearchQuery,
): {
  searchKey: string;
  query: Partial<UnparsedUserSavedSearchQuery>;
} => {
  const price = cleanObj({
    min: userSavedSearchQueryParams.minPrice,
    max: userSavedSearchQueryParams.maxPrice,
  });

  return {
    searchKey: (userSavedSearchQueryParams.searchText || '').trim(),
    query: deepCleanObj(
      {
        categories: userSavedSearchQueryParams.categoryIds?.[0],
        conditions: userSavedSearchQueryParams.conditionIds?.length ? JSON.stringify(userSavedSearchQueryParams.conditionIds) : null,
        colors: userSavedSearchQueryParams.colorIds?.length ? JSON.stringify(userSavedSearchQueryParams.colorIds) : null,
        brands: userSavedSearchQueryParams.brandIds?.length ? JSON.stringify(userSavedSearchQueryParams.brandIds) : null,
        sizes: userSavedSearchQueryParams.sizes?.join('.'),
        price: Object.keys(price).length ? JSON.stringify(price) : null,
        order: userSavedSearchQueryParams.order,
        tag: userSavedSearchQueryParams.tag,
        materials: userSavedSearchQueryParams.materialIds?.length ? JSON.stringify(userSavedSearchQueryParams.materialIds) : null,
      },
      {
        includeNull: true,
        includeUndefined: true,
        includeEmptyArr: true,
        includeEmptyObj: true,
        includeEmptyString: true,
      },
    ),
  };
};
